// The authoritative match: createMatch, validate (never mutates) and apply (mutates, returns events).
// The room calls apply(state, { t: 'sys.tick' }) every 50 ms and a sys.deadline for each deadline
// in state.deadlines. Nothing here reads the clock or Math.random.
import { KILL_RANGE_TILES } from '@wahala/config';
import { getMap, TILE, taskStations } from '@wahala/maps';
import { createAreaBoys } from './areaboys.js';
import { arrestTimedOut, checkCut, checkTap, createArrests, cutPadlock, jailed, locked, startArrest, stopArrests, tapNeedle, tickArrests, } from './arrest.js';
import { clearCrashes, collideKekes, createCrashes, KNOCK_SHOVE, SHAKEN_FACTOR, shaken } from './crashes.js';
import { BASE_SPEED, dist2, GHOST_SPEED_FACTOR, inputOk, PLAYER_RADIUS, slide, stepPlayer, TICK_MS, } from './geometry.js';
import { deal, solved } from './minigames.js';
import { canUsePower, dropDecoyOf, usePower } from './powers.js';
import { checkRide, createRides, emptyKeke, rideStop, tickRides } from './rides.js';
import { nextInt, seedRng, shuffled } from './rng.js';
import { dealRoles } from './roles.js';
import { rulesFor } from './rules.js';
import { CRITICAL_DEADLINE, checkFix, checkStart, fixSabotage, isCritical, pauseForMeeting, resumeAfterMeeting, startSabotage, } from './sabotage.js';
import { checkVent, enterVent, exitVent, moveVent } from './vents.js';
import { createDaylight, createWeather, tickDaylight, tickWeather, weatherBrake } from './weather.js';
export const STATION_RADIUS = 1.5 * TILE;
export const REPORT_RADIUS = 2 * TILE;
export const BUTTON_RADIUS = 1.5 * TILE;
export const SESSION_TTL_MS = 60_000;
const ok = { ok: true };
const no = (reason) => ({ ok: false, reason });
/** Ids that come off the wire are only ever looked up as own keys, never through the prototype. */
const own = (rec, key) => Object.hasOwn(rec, key) ? rec[key] : undefined;
/** Seat ids become object keys; keep them to plain identifiers. */
export const SEAT_ID = /^[A-Za-z0-9_-]{1,64}$/;
export const mapOf = (state) => getMap(state.mapId);
/**
 * How many agberos a park of n gets: never more than the config allows, one below 8 players,
 * two from 8, three from 12 (design system Game UX), and always fewer than half the park.
 */
export function impostorsFor(cfg, n) {
    // a race has no agberos at all
    if (cfg.impostorCount <= 0)
        return 0;
    const bySize = n >= 12 ? 3 : n >= 8 ? 2 : 1;
    return Math.max(1, Math.min(cfg.impostorCount, bySize, Math.floor((n - 1) / 2)));
}
function assignTasks(state, map) {
    const { taskCounts } = state.config;
    const byId = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    const common = map.tasks
        .filter((t) => t.kind === 'common')
        .sort(byId)
        .slice(0, taskCounts.common);
    const commonStations = new Set(common.flatMap(taskStations));
    for (const id of state.order) {
        const p = state.players[id];
        const used = new Set(commonStations);
        const picked = [...common];
        for (const kind of ['short', 'long']) {
            const pool = shuffled(state.rng, map.tasks.filter((t) => t.kind === kind).sort(byId));
            let need = taskCounts[kind];
            for (const t of pool) {
                if (need === 0)
                    break;
                // no two stops of one driver at the same place, counting every stage's
                const places = taskStations(t);
                if (places.some((x) => used.has(x)))
                    continue;
                for (const x of places)
                    used.add(x);
                picked.push(t);
                need--;
            }
        }
        state.tasks[id] = picked.map((t) => ({
            id: t.id,
            stationId: t.stationId,
            label: t.label,
            kind: t.kind,
            holdMs: t.holdMs,
            ...(t.game && t.game !== 'hold' ? { game: t.game } : {}),
            ...(t.stages?.length ? { stage: 0, stages: t.stages.length + 1, readyAt: 0 } : {}),
            done: false,
            fake: p.role === 'impostor',
        }));
        state.sessions[id] = null;
    }
}
/**
 * Move a stop with stages on to its next stage, if it has one: the next stage's place, job, floor
 * and game, opening after its wait. False when the stage just done was the last.
 */
function nextStage(s, task) {
    if (task.stage === undefined || task.stages === undefined || task.stage + 1 >= task.stages)
        return false;
    const next = mapOf(s).tasks.find((t) => t.id === task.id)?.stages?.[task.stage];
    if (!next)
        return false;
    task.stage++;
    task.stationId = next.stationId;
    task.label = next.label;
    task.holdMs = next.holdMs;
    if (next.game && next.game !== 'hold')
        task.game = next.game;
    else
        delete task.game;
    task.readyAt = s.now + (next.afterMs ?? 0);
    return true;
}
/** Create a match that starts on the road. Seats come in seat order. */
/** The most a role ticket can tilt the agbero draw: twice the chance of an unarmed seat. */
export const MAX_ROLE_WEIGHT = 2;
/**
 * Pick the agberos. With every weight equal (nobody armed, or everybody) this is the plain seeded
 * shuffle, so tickets change nothing then. Otherwise each pick is a seeded draw in proportion to
 * the remaining weights, in seat order: integers only, the same on every machine.
 */
export function drawImpostors(rng, seats, count) {
    // Solo's side pick: a chosen agbero is one for sure, a chosen driver never is; the rest draw
    if (seats.some((s) => s.side)) {
        const sure = seats.filter((s) => s.side === 'impostor').slice(0, count);
        const rest = drawImpostors(rng, seats.filter((s) => !s.side).map(({ side: _, ...s }) => s), count - sure.length);
        return new Set([...sure.map((s) => s.id), ...rest]);
    }
    const weight = (s) => Math.min(MAX_ROLE_WEIGHT, Math.max(1, Math.floor(s.weight ?? 1)));
    const ws = seats.map(weight);
    if (ws.every((w) => w === ws[0]))
        return new Set(shuffled(rng, seats.map((s) => s.id)).slice(0, count));
    const pool = seats.map((s, i) => ({ id: s.id, w: ws[i] }));
    const out = new Set();
    while (out.size < count && pool.length) {
        let pick = nextInt(rng, pool.reduce((t, x) => t + x.w, 0));
        let i = 0;
        while (pick >= pool[i].w) {
            pick -= pool[i].w;
            i++;
        }
        out.add(pool[i].id);
        pool.splice(i, 1);
    }
    return out;
}
export function createMatch(config, seats, seed, now) {
    const map = getMap(config.mapId);
    if (seats.length < config.minPlayers || seats.length > config.maxPlayers) {
        throw new RangeError(`need ${config.minPlayers}-${config.maxPlayers} players, got ${seats.length}`);
    }
    if (new Set(seats.map((s) => s.id)).size !== seats.length)
        throw new Error('duplicate seat ids');
    for (const seat of seats)
        if (!SEAT_ID.test(seat.id) || seat.id in Object.prototype)
            throw new Error('seat ids must be plain identifiers');
    const rng = seedRng(seed);
    const order = seats.map((s) => s.id);
    const impostorIds = drawImpostors(rng, seats, impostorsFor(config, seats.length));
    const players = {};
    seats.forEach((s, i) => {
        const role = impostorIds.has(s.id) ? 'impostor' : 'crew';
        const spawn = map.spawns[i % map.spawns.length];
        players[s.id] = {
            id: s.id,
            seat: i,
            colour: s.colour,
            name: s.name,
            role,
            team: role,
            alive: true,
            connected: true,
            departed: false,
            ejected: false,
            deathRevealed: false,
            diedAt: null,
            pos: { x: spawn.x, y: spawn.y },
            vel: { x: 0, y: 0 },
            input: { dx: 0, dy: 0 },
            lastInputSeq: 0,
            killReadyAt: now + config.killCooldownMs,
            meetingsLeft: config.meetingsPerPlayer,
            vent: null,
            ventCooldownUntil: now,
            vote: null,
            stats: { tasksDone: 0, correctVotes: 0, reports: 0, kills: 0 },
        };
    });
    const state = {
        version: 1,
        seed,
        tick: 0,
        now,
        phase: 'PLAYING',
        rng,
        config: JSON.parse(JSON.stringify(config)),
        mapId: map.id,
        players,
        order,
        bodies: [],
        tasks: {},
        sessions: {},
        taskProgress: 0,
        shownProgress: 0,
        meeting: null,
        meetingCooldownUntil: now + config.meetingCooldownMs,
        sabotage: null,
        sabotageReadyAt: now + config.sabotageCooldownMs,
        deadlines: {},
        firstMeetingHeld: false,
        impostorDepartedBeforeFirstMeeting: false,
        winner: null,
    };
    // special roles, right after the agbero draw (no draw at all when the park has none on)
    dealRoles(state);
    assignTasks(state, map);
    recomputeProgress(state);
    rulesFor(state).onMatchStart(state, seats);
    // passengers and solid kekes: their own state (and generator), so no other draw moves
    if (rulesFor(state).rides)
        state.rides = createRides(state);
    state.crashes = createCrashes();
    // the weather draws from its own generator too
    state.weather = createWeather(state);
    state.daylight = createDaylight(state);
    // area boys work the fares, so only where passengers ride (their own generator too)
    const boys = state.rides ? createAreaBoys(state) : undefined;
    if (boys)
        state.areaBoys = boys;
    // arrests and the yard (config.arrest)
    const arrests = createArrests(state);
    if (arrests)
        state.arrest = arrests;
    return state;
}
// ---------------------------------------------------------------- helpers
const playersInOrder = (s) => s.order.map((id) => s.players[id]);
export function recomputeProgress(s) {
    let total = 0;
    let done = 0;
    for (const p of playersInOrder(s)) {
        if (p.role !== 'crew' || p.departed)
            continue;
        for (const t of s.tasks[p.id] ?? []) {
            if (t.fake)
                continue;
            total++;
            if (t.done)
                done++;
        }
    }
    s.taskProgress = total === 0 ? 0 : done / total;
}
/**
 * Move the progress players see, per config.taskBar, and announce it only when it moves. The real
 * progress still decides the win; this is only what reaches the screens.
 */
function showProgress(s, events, meetingOpened = false) {
    // Network don go: the route bar holds still until the mast is reset (a meeting still shows it)
    if (s.sabotage?.kind === 'network' && !meetingOpened)
        return;
    const mode = s.config.taskBar;
    const next = mode === 'always' || (mode === 'meetings' && meetingOpened) ? s.taskProgress : s.shownProgress;
    if (next === s.shownProgress)
        return;
    s.shownProgress = next;
    events.push({ t: 'task.progress', progress: next });
}
/** Has a team won? The mode's rule set decides (rules.ts); the first rule that matches wins. */
export function checkWin(s) {
    return rulesFor(s).checkWin(s);
}
/** Record a win. During a vote reveal the match ends when the reveal finishes. */
/** What may still be done arrested or in the yard: keep the connection, leave, tap the needle. */
const WHILE_LOCKED = new Set([
    'player.input',
    'player.connection',
    'player.depart',
    'player.halt',
    'arrest.tap',
]);
/**
 * After an arrest ends: a driver taken to the yard leaves their keke clamped (its riders run off),
 * the special roles hear of it as of a knock, and the win check runs.
 */
function arrested(s, events) {
    for (const e of events) {
        if (e.t !== 'arrest.ended' || e.outcome !== 'yard')
            continue;
        const target = s.players[e.playerId];
        emptyKeke(s, target.id);
        dropDecoyOf(s, target.id);
        // heard where it happened (the clamped keke), not in the yard the driver was taken to
        const clamp = s.bodies.find((b) => b.clamp && b.playerId === target.id);
        rulesFor(s).onKnocked(s, clamp ? { ...target, pos: { ...clamp.pos } } : target, events);
    }
    settleWin(s, events);
}
function settleWin(s, events) {
    if (s.winner)
        return;
    const w = checkWin(s);
    if (!w)
        return;
    s.winner = w;
    if (s.phase !== 'RESOLUTION')
        endMatch(s, events);
}
function endMatch(s, events) {
    if (!s.winner)
        return;
    s.phase = 'ENDED';
    s.deadlines = {};
    s.meeting = null;
    for (const p of playersInOrder(s)) {
        p.input = { dx: 0, dy: 0 };
        p.vel = { x: 0, y: 0 };
    }
    events.push({ t: 'match.ended', team: s.winner.team, reason: s.winner.reason });
}
function startMeeting(s, calledBy, reason, bodyId, events) {
    const c = s.config;
    for (const p of playersInOrder(s)) {
        if (!p.alive)
            p.deathRevealed = true;
        exitVent(s, p, null); // a meeting pulls everyone out of the shortcuts
        p.input = { dx: 0, dy: 0 };
        p.vel = { x: 0, y: 0 };
        p.vote = null;
        s.sessions[p.id] = null;
    }
    // an arrest under way ends with the driver let go; the yard is shown to the park
    stopArrests(s, events);
    s.bodies = [];
    clearCrashes(s);
    rulesFor(s).onMeeting(s);
    pauseForMeeting(s, events);
    s.firstMeetingHeld = true;
    s.meeting = {
        calledBy,
        reason,
        bodyId,
        discussionEndsAt: s.now + c.discussionMs,
        votingEndsAt: s.now + c.discussionMs + c.votingMs,
        revealEndsAt: null,
        result: null,
    };
    events.push({ t: 'meeting.called', by: calledBy, reason });
    showProgress(s, events, true);
    if (c.discussionMs === 0) {
        s.phase = 'VOTING';
        s.deadlines = { 'meeting.voting': s.meeting.votingEndsAt };
        events.push({ t: 'meeting.voting' });
    }
    else {
        s.phase = 'DISCUSSION';
        s.deadlines = { 'meeting.discussion': s.meeting.discussionEndsAt };
    }
}
/** Highest single count wins; skip leading, or any tie for the top, ejects nobody. */
export function tallyVotes(s) {
    const counts = new Map();
    for (const p of playersInOrder(s)) {
        if (!p.alive || p.vote === null)
            continue;
        counts.set(p.vote, (counts.get(p.vote) ?? 0) + 1);
    }
    const options = [
        ...s.order.filter((id) => counts.has(id)),
        ...(counts.has('skip') ? ['skip'] : []),
    ];
    const tally = options.map((option) => ({ option, votes: counts.get(option) ?? 0 }));
    let best = 0;
    for (const t of tally)
        best = Math.max(best, t.votes);
    const top = tally.filter((t) => t.votes === best && best > 0);
    const ejected = top.length === 1 && top[0]?.option !== 'skip' ? top[0]?.option : null;
    return { ejected, tally };
}
function resolveVotes(s, events) {
    if (!s.meeting)
        return;
    const result = tallyVotes(s);
    if (result.ejected) {
        const out = s.players[result.ejected];
        out.alive = false;
        out.ejected = true;
        out.deathRevealed = true;
        out.diedAt = s.now;
        s.sessions[out.id] = null;
        emptyKeke(s, out.id);
        if (out.role === 'impostor') {
            for (const p of playersInOrder(s)) {
                if (p.alive && p.vote === out.id)
                    p.stats.correctVotes++;
            }
        }
    }
    s.meeting.result = result;
    s.meeting.revealEndsAt = s.now + s.config.revealMs;
    s.phase = 'RESOLUTION';
    s.deadlines = { 'meeting.reveal': s.meeting.revealEndsAt };
    events.push({ t: 'vote.result', ejected: result.ejected });
    settleWin(s, events);
}
function endMeeting(s, events) {
    if (s.winner) {
        endMatch(s, events);
        return;
    }
    const map = mapOf(s);
    let i = 0;
    for (const p of playersInOrder(s)) {
        p.vote = null;
        // the locked up stay in the yard
        if (!p.alive || jailed(s, p.id))
            continue;
        const spot = map.spawns[i++ % map.spawns.length];
        p.pos = { x: spot.x, y: spot.y };
        p.vel = { x: 0, y: 0 };
        if (p.role === 'impostor')
            p.killReadyAt = s.now + s.config.killCooldownMs;
    }
    s.meeting = null;
    s.phase = 'PLAYING';
    s.deadlines = {};
    resumeAfterMeeting(s);
    s.meetingCooldownUntil = s.now + s.config.meetingCooldownMs;
    rulesFor(s).onMeetingEnd(s);
    events.push({ t: 'meeting.ended' });
}
const allVoted = (s) => playersInOrder(s)
    .filter((p) => p.alive && p.connected && !jailed(s, p.id))
    .every((p) => p.vote !== null);
function stationPos(map, stationId) {
    return map.stations.find((st) => st.id === stationId)?.pos ?? null;
}
// ---------------------------------------------------------------- validate
export function validate(s, cmd) {
    if (cmd.t === 'sys.tick')
        return s.phase === 'ENDED' ? no('MATCH_OVER') : ok;
    if (cmd.t === 'sys.deadline') {
        const at = own(s.deadlines, cmd.id);
        if (at === undefined)
            return no('NO_SUCH_DEADLINE');
        return s.now >= at ? ok : no('TOO_SOON');
    }
    if (s.phase === 'ENDED')
        return no('MATCH_OVER');
    const p = own(s.players, cmd.playerId);
    if (!p)
        return no('UNKNOWN_PLAYER');
    if (p.departed)
        return no('DEPARTED');
    const map = getMap(s.mapId);
    // in a shortcut: nothing but crossing or climbing out
    const blockedInVent = [
        'player.report',
        'player.button',
        'task.interact',
        'task.complete',
        'sabotage.fix',
        'power.use',
        'ability.use',
        'ride.stop',
    ];
    if (p.vent !== null && blockedInVent.includes(cmd.t))
        return no('IN_VENT');
    // arrested or in the yard: nothing but waiting (and the driver's tap on the needle)
    const lock = locked(s, p.id);
    if (lock && !WHILE_LOCKED.has(cmd.t))
        return no(lock);
    const c = s.config;
    const rules = rulesFor(s);
    // what the park's mode does not have at all (hide and seek: no horn, reports, votes, wahala)
    const notHere = rules.refuse(s, p, cmd.t);
    if (notHere)
        return no(notHere);
    switch (cmd.t) {
        case 'player.connection':
        case 'player.depart':
        case 'player.halt':
            return ok;
        case 'vent.enter':
        case 'vent.move': {
            const action = cmd.t === 'vent.enter' ? 'enter' : 'move';
            const r = checkVent(s, p, action, cmd.ventId, rules.mayVent(s, p, action));
            return r ? no(r) : ok;
        }
        case 'vent.exit': {
            const r = checkVent(s, p, 'exit', undefined, rules.mayVent(s, p, 'exit'));
            return r ? no(r) : ok;
        }
        case 'sabotage.start': {
            const r = checkStart(s, p, cmd.kind);
            return r ? no(r) : ok;
        }
        case 'sabotage.fix': {
            const r = checkFix(s, p, cmd.stationId);
            return r ? no(r) : ok;
        }
        case 'power.use':
            if (s.phase !== 'PLAYING')
                return no('WRONG_PHASE');
            if (!p.alive)
                return no('NOT_ALIVE');
            if (p.vent !== null)
                return no('IN_VENT');
            return canUsePower(s, p) ? ok : no('NO_POWER');
        case 'ride.stop': {
            const r = checkRide(s, p);
            return r ? no(r) : ok;
        }
        case 'boys.answer':
        case 'boys.send': {
            const r = rules.checkBoys(s, p, cmd);
            return r ? no(r) : ok;
        }
        // a mode's own commands (a race's pickup disc and Go); a mode without them has none
        case 'race.use':
        case 'race.go':
        case 'race.foot':
        case 'race.horn':
        case 'hustle.take':
        case 'hustle.drop':
        case 'hustle.buy':
        case 'hustle.bank':
        case 'hustle.settle':
        case 'hustle.police':
        case 'hustle.tap': {
            const r = rules.checkCommand ? rules.checkCommand(s, p, cmd) : 'NOT_IN_MODE';
            return r ? no(r) : ok;
        }
        case 'player.input':
            if (!inputOk(cmd.dx, cmd.dy))
                return no('BAD_INPUT');
            return cmd.seq > p.lastInputSeq ? ok : no('STALE_INPUT');
        case 'player.kill': {
            if (s.phase !== 'PLAYING')
                return no('WRONG_PHASE');
            if (p.role !== 'impostor')
                return no('NOT_IMPOSTOR');
            if (!p.alive)
                return no('NOT_ALIVE');
            if (p.vent !== null)
                return no('IN_VENT');
            if (s.now < p.killReadyAt)
                return no('COOLDOWN');
            const target = own(s.players, cmd.targetId);
            if (!target || target.departed || !target.alive || target.team !== 'crew')
                return no('BAD_TARGET');
            if (locked(s, target.id))
                return no('BAD_TARGET');
            if (target.vent !== null)
                return no('IN_VENT');
            const range = KILL_RANGE_TILES[c.killRange] * TILE;
            if (dist2(p.pos, target.pos) > range * range)
                return no('OUT_OF_RANGE');
            const refused = rules.mayKnock(s, p, target);
            return refused ? no(refused) : ok;
        }
        case 'ability.use': {
            const r = rules.checkAbility(s, p, cmd.targetId);
            return r ? no(r) : ok;
        }
        case 'player.report': {
            if (s.phase !== 'PLAYING')
                return no('WRONG_PHASE');
            if (!p.alive)
                return no('NOT_ALIVE');
            const body = s.bodies.find((b) => b.id === cmd.bodyId);
            if (!body)
                return no('NO_SUCH_BODY');
            if (body.reported)
                return no('ALREADY_REPORTED');
            return dist2(p.pos, body.pos) <= REPORT_RADIUS * REPORT_RADIUS ? ok : no('OUT_OF_RANGE');
        }
        case 'player.button': {
            if (s.phase !== 'PLAYING')
                return no('WRONG_PHASE');
            if (!p.alive)
                return no('NOT_ALIVE');
            if (p.meetingsLeft <= 0)
                return no('NO_MEETINGS_LEFT');
            if (s.now < s.meetingCooldownUntil)
                return no('COOLDOWN');
            if (isCritical(s))
                return no('SABOTAGE_ACTIVE');
            const refused = rules.canCallMeeting(s, p);
            if (refused)
                return no(refused);
            const button = map.stations.find((st) => st.kind === 'button');
            if (!button)
                return no('OUT_OF_RANGE');
            return dist2(p.pos, button.pos) <= BUTTON_RADIUS * BUTTON_RADIUS ? ok : no('OUT_OF_RANGE');
        }
        case 'task.interact': {
            if (s.phase !== 'PLAYING')
                return no('WRONG_PHASE');
            const task = s.tasks[p.id]?.find((t) => t.id === cmd.taskId);
            if (!task)
                return no('NOT_ASSIGNED');
            if (task.done)
                return no('ALREADY_DONE');
            // a stop already running stays as it is (the client resumes its hold); another stop replaces it
            const open = s.sessions[p.id];
            if (open && open.taskId === cmd.taskId && s.now <= open.startedAt + SESSION_TTL_MS)
                return no('SESSION_OPEN');
            // a later stage opens only once its wait is over (the oil has drained)
            if (s.now < (task.readyAt ?? 0))
                return no('TOO_SOON');
            const at = stationPos(map, task.stationId);
            if (!at || dist2(p.pos, at) > STATION_RADIUS * STATION_RADIUS)
                return no('OUT_OF_RANGE');
            return ok;
        }
        case 'task.complete': {
            if (s.phase !== 'PLAYING')
                return no('WRONG_PHASE');
            const task = s.tasks[p.id]?.find((t) => t.id === cmd.taskId);
            if (!task)
                return no('NOT_ASSIGNED');
            if (task.done)
                return no('ALREADY_DONE');
            const session = s.sessions[p.id];
            if (!session || session.taskId !== cmd.taskId)
                return no('NO_SESSION');
            if (s.now > session.startedAt + SESSION_TTL_MS)
                return no('SESSION_EXPIRED');
            if (s.now < session.startedAt + task.holdMs)
                return no('TOO_SOON');
            // the load game's dealt wait is a floor too: early is too soon, not a wrong answer
            if (session.waitMs !== undefined && s.now < session.startedAt + session.waitMs)
                return no('TOO_SOON');
            const at = stationPos(map, task.stationId);
            if (!at || dist2(p.pos, at) > STATION_RADIUS * STATION_RADIUS)
                return no('OUT_OF_RANGE');
            // the game's answer, checked against what the server dealt (the stop stays open on a miss)
            return solved(task, session, cmd.outcome, s.now - session.startedAt) ? ok : no('BAD_OUTCOME');
        }
        case 'meeting.vote': {
            if (s.phase !== 'VOTING')
                return no('WRONG_PHASE');
            if (!p.alive)
                return no('NOT_ALIVE');
            if (p.vote !== null)
                return no('ALREADY_VOTED');
            if (!rules.canVote(s, p))
                return no('WRONG_PHASE');
            if (cmd.target === 'skip')
                return ok;
            const target = own(s.players, cmd.target);
            // nobody votes a driver out of the yard
            return target?.alive && !jailed(s, target.id) ? ok : no('BAD_TARGET');
        }
        case 'arrest.tap': {
            const r = checkTap(s, p);
            return r ? no(r) : ok;
        }
        case 'breakout.cut': {
            const r = checkCut(s, p);
            return r ? no(r) : ok;
        }
    }
}
// ---------------------------------------------------------------- apply
/** Apply a command that already passed validate. Returns the events it caused. */
export function apply(s, cmd) {
    const events = [];
    const map = mapOf(s);
    switch (cmd.t) {
        case 'sys.tick': {
            s.tick++;
            s.now += TICK_MS;
            if (s.phase !== 'PLAYING')
                break;
            const speed = Math.round(BASE_SPEED * s.config.playerSpeed);
            const rules = rulesFor(s);
            const brake = weatherBrake(s);
            for (const p of playersInOrder(s)) {
                if (p.departed || p.vent !== null)
                    continue;
                // arrested or in the yard: standing still
                if (locked(s, p.id)) {
                    p.vel = { x: 0, y: 0 };
                    continue;
                }
                // a mode that drives its own kekes (a race keke drives itself forward)
                if (rules.drive) {
                    rules.drive(s, p, events);
                    continue;
                }
                const ghost = !p.alive;
                const base = ghost ? Math.round(speed * GHOST_SPEED_FACTOR) : rules.speed(s, p, speed);
                // shaken after a crash: half speed for a moment
                const v = !ghost && shaken(s, p.id) ? Math.round(base * SHAKEN_FACTOR) : base;
                const k = stepPlayer(p, p.input, map, v, ghost, brake);
                p.pos = k.pos;
                p.vel = k.vel;
            }
            collideKekes(s, events);
            tickRides(s);
            tickArrests(s);
            rules.onTick(s, events);
            tickWeather(s, rules.holdWeather?.(s) ?? false);
            tickDaylight(s);
            break;
        }
        case 'sys.deadline': {
            delete s.deadlines[cmd.id];
            if (cmd.id.startsWith('arrest:') && s.phase === 'PLAYING') {
                // the officer's patience ran out: no tap is wahala
                arrestTimedOut(s, cmd.id.slice('arrest:'.length), events);
                arrested(s, events);
            }
            else if (cmd.id === 'meeting.discussion' && s.phase === 'DISCUSSION' && s.meeting) {
                s.phase = 'VOTING';
                s.deadlines['meeting.voting'] = s.meeting.votingEndsAt;
                events.push({ t: 'meeting.voting' });
            }
            else if (cmd.id === 'meeting.voting' && s.phase === 'VOTING') {
                resolveVotes(s, events);
            }
            else if (cmd.id === 'meeting.reveal' && s.phase === 'RESOLUTION') {
                endMeeting(s, events);
            }
            else if (cmd.id === CRITICAL_DEADLINE && s.phase === 'PLAYING' && isCritical(s) && !s.winner) {
                // the critical wahala ran out: the agberos win (fixed rule 2, before parity and stops)
                s.winner = { team: 'impostor', reason: 'sabotage' };
                endMatch(s, events);
            }
            else if (s.phase === 'PLAYING' && !s.winner) {
                // the mode's own clocks (hide and seek: the drivers lasted)
                const w = rulesFor(s).onDeadline(s, cmd.id);
                if (w) {
                    s.winner = w;
                    endMatch(s, events);
                }
            }
            break;
        }
        case 'player.input': {
            const p = s.players[cmd.playerId];
            p.lastInputSeq = cmd.seq;
            p.input = { dx: cmd.dx, dy: cmd.dy, ...(cmd.brake ? { brake: true } : {}) };
            break;
        }
        case 'player.kill': {
            const p = s.players[cmd.playerId];
            const target = s.players[cmd.targetId];
            // where arrests are on, the knock is an arrest: Bribe or Bail decides
            if (s.arrest) {
                startArrest(s, p, target, events);
                break;
            }
            target.alive = false;
            target.diedAt = s.now;
            target.input = { dx: 0, dy: 0 };
            target.vel = { x: 0, y: 0 };
            s.sessions[target.id] = null;
            dropDecoyOf(s, target.id);
            emptyKeke(s, target.id);
            // a ram: the keke hit is shoved on along the line of the hit, and the agbero ends up nose to
            // the wreck (never on top of it), stopped
            const dx = target.pos.x - p.pos.x;
            const dy = target.pos.y - p.pos.y;
            const d = Math.sqrt(dx * dx + dy * dy);
            const nx = d === 0 ? 1 : dx / d;
            const ny = d === 0 ? 0 : dy / d;
            target.pos = slide(map, target.pos, Math.round(nx * KNOCK_SHOVE), Math.round(ny * KNOCK_SHOVE));
            const gap = Math.sqrt(dist2(p.pos, target.pos));
            const contact = 2 * PLAYER_RADIUS;
            if (gap > contact) {
                const k = (gap - contact) / gap;
                p.pos = slide(map, p.pos, Math.round((target.pos.x - p.pos.x) * k), Math.round((target.pos.y - p.pos.y) * k));
            }
            s.bodies.push({ id: target.id, playerId: target.id, pos: { ...target.pos }, createdAt: s.now, reported: false });
            p.vel = { x: 0, y: 0 };
            p.killReadyAt = s.now + s.config.killCooldownMs;
            p.stats.kills++;
            events.push({ t: 'player.killed', playerId: target.id, by: p.id, pos: { ...target.pos } });
            rulesFor(s).onKnocked(s, target, events);
            settleWin(s, events);
            break;
        }
        case 'player.report': {
            const p = s.players[cmd.playerId];
            const body = s.bodies.find((b) => b.id === cmd.bodyId);
            if (body?.decoy) {
                // a fake breakdown: it clears, the reporter is told, and nobody is called to the park
                s.bodies = s.bodies.filter((b) => b !== body);
                events.push({ t: 'decoy.reported', reporter: p.id, bodyId: body.id });
                break;
            }
            if (body)
                body.reported = true;
            p.stats.reports++;
            events.push({ t: 'body.reported', reporter: p.id, bodyId: cmd.bodyId });
            startMeeting(s, p.id, 'report', cmd.bodyId, events);
            break;
        }
        case 'player.button': {
            const p = s.players[cmd.playerId];
            p.meetingsLeft--;
            startMeeting(s, p.id, 'button', null, events);
            break;
        }
        case 'task.interact': {
            const task = s.tasks[cmd.playerId]?.find((t) => t.id === cmd.taskId);
            s.sessions[cmd.playerId] = { taskId: cmd.taskId, startedAt: s.now, ...deal(s, task) };
            break;
        }
        case 'task.complete': {
            const p = s.players[cmd.playerId];
            const task = s.tasks[p.id]?.find((t) => t.id === cmd.taskId);
            s.sessions[p.id] = null;
            // a stage before the last: on to the next one (the same for a fake copy, so it looks the same)
            if (nextStage(s, task))
                break;
            task.done = true;
            // a fake task is accepted and discarded: the agbero's screen looks normal, the route bar never moves
            if (!task.fake) {
                p.stats.tasksDone++;
                recomputeProgress(s);
                rulesFor(s).onTaskDone(s, p);
                events.push({ t: 'task.completed', playerId: p.id, taskId: task.id });
                showProgress(s, events);
                settleWin(s, events);
            }
            break;
        }
        case 'meeting.vote': {
            const p = s.players[cmd.playerId];
            p.vote = cmd.target;
            events.push({ t: 'vote.cast', playerId: p.id });
            if (allVoted(s)) {
                delete s.deadlines['meeting.voting'];
                resolveVotes(s, events);
            }
            break;
        }
        case 'player.halt': {
            s.players[cmd.playerId].input = { dx: 0, dy: 0 };
            break;
        }
        case 'ride.stop': {
            const p = s.players[cmd.playerId];
            rideStop(s, p, events);
            rulesFor(s).onRideStop(s, p, events);
            break;
        }
        case 'boys.answer':
        case 'boys.send':
            rulesFor(s).useBoys(s, s.players[cmd.playerId], cmd, events);
            break;
        case 'arrest.tap':
            tapNeedle(s, s.players[cmd.playerId], cmd.at, events, cmd.lagMs);
            arrested(s, events);
            break;
        case 'breakout.cut':
            cutPadlock(s, s.players[cmd.playerId], events);
            settleWin(s, events);
            break;
        case 'race.use':
        case 'race.go':
        case 'race.foot':
        case 'race.horn':
        case 'hustle.take':
        case 'hustle.drop':
        case 'hustle.buy':
        case 'hustle.bank':
        case 'hustle.settle':
        case 'hustle.police':
        case 'hustle.tap':
            rulesFor(s).applyCommand?.(s, s.players[cmd.playerId], cmd, events);
            break;
        case 'power.use':
            usePower(s, s.players[cmd.playerId], events);
            break;
        case 'ability.use':
            rulesFor(s).useAbility(s, s.players[cmd.playerId], cmd.targetId, events);
            break;
        case 'vent.enter': {
            const p = s.players[cmd.playerId];
            enterVent(s, p, cmd.ventId, events);
            rulesFor(s).onVentEnter(s, p);
            break;
        }
        case 'vent.move':
            moveVent(s, s.players[cmd.playerId], cmd.ventId, events);
            break;
        case 'vent.exit':
            exitVent(s, s.players[cmd.playerId], events);
            break;
        case 'sabotage.start':
            startSabotage(s, s.players[cmd.playerId], cmd.kind, events);
            break;
        case 'sabotage.fix': {
            fixSabotage(s, s.players[cmd.playerId], cmd.stationId, events);
            showProgress(s, events); // a reset mast lets the route bar catch up
            break;
        }
        case 'player.connection': {
            const p = s.players[cmd.playerId];
            p.connected = cmd.connected;
            if (!cmd.connected)
                p.input = { dx: 0, dy: 0 };
            events.push({ t: 'player.connection', playerId: p.id, connected: cmd.connected });
            if (!cmd.connected && s.phase === 'VOTING' && allVoted(s)) {
                delete s.deadlines['meeting.voting'];
                resolveVotes(s, events);
            }
            break;
        }
        case 'player.depart': {
            const p = s.players[cmd.playerId];
            // a seat that ran out may be a dropped line (a draw is fair); a chosen leave is a forfeit
            if (p.role === 'impostor' && !s.firstMeetingHeld && !cmd.left)
                s.impostorDepartedBeforeFirstMeeting = true;
            p.departed = true;
            p.connected = false;
            if (p.alive) {
                p.alive = false;
                p.diedAt = s.now;
            }
            p.deathRevealed = true;
            p.input = { dx: 0, dy: 0 };
            s.sessions[p.id] = null;
            emptyKeke(s, p.id);
            recomputeProgress(s);
            showProgress(s, events);
            events.push({ t: 'player.departed', playerId: p.id });
            if (s.phase === 'VOTING' && allVoted(s)) {
                delete s.deadlines['meeting.voting'];
                resolveVotes(s, events);
            }
            settleWin(s, events);
            break;
        }
    }
    return events;
}
/** Validate then apply. Rejections come back as a verdict with no state change. */
export function step(s, cmd) {
    const verdict = validate(s, cmd);
    return verdict.ok ? { verdict, events: apply(s, cmd) } : { verdict, events: [] };
}
/** Deadlines that are due now, earliest first (the room turns each into a sys.deadline). */
export function dueDeadlines(s) {
    return Object.entries(s.deadlines)
        .filter(([, at]) => at <= s.now)
        .sort((a, b) => a[1] - b[1] || (a[0] < b[0] ? -1 : 1))
        .map(([id]) => id);
}
/** Small helper for tests and bots: a uniform choice using the match's own RNG would change the
 *  match, so callers pass their own RNG state. */
export function pick(rng, items) {
    return items.length === 0 ? undefined : items[nextInt(rng, items.length)];
}
//# sourceMappingURL=match.js.map